/**
 * useAccordionState Hook
 * Shared state management for accordion sections
 */

import { useState, useCallback, useEffect, useRef } from 'react';

export interface AccordionStateReturn {
  expandedSections: Set<string>;
  toggleSection: (sectionId: string) => void;
  expandSection: (sectionId: string) => void;
  collapseSection: (sectionId: string) => void;
  expandAll: (sectionIds: string[]) => void;
  collapseAll: () => void;
  isExpanded: (sectionId: string) => boolean;
  isAnimating: (sectionId: string) => boolean; // True if section was just expanded by user click
}

/**
 * Custom hook for managing accordion section state
 * @param defaultExpandedSections - Array of section IDs to expand by default
 * @param onChange - Optional callback when expanded sections change (for persistence)
 * @returns Object with state and control methods
 */
export const useAccordionState = (
  defaultExpandedSections: string[] = [],
  onChange?: (expandedSections: Set<string>) => void
): AccordionStateReturn => {
  // Use lazy initialization to ensure default sections are applied on every mount
  const [expandedSections, setExpandedSections] = useState<Set<string>>(() => {
    return new Set(defaultExpandedSections);
  });

  // Track sections that are currently animating (expanded by user click)
  const [animatingSections, setAnimatingSections] = useState<Set<string>>(new Set());

  // Track if state was changed by user interaction (not by defaultExpandedSections update)
  const userInteractionRef = useRef(false);

  // Update expanded sections when defaultExpandedSections changes (e.g., when state loads from localStorage)
  // Only update if the content actually changed to avoid infinite loops
  useEffect(() => {
    const newSet = new Set(defaultExpandedSections);

    // Use functional update to access current state without adding it as a dependency
    setExpandedSections(currentSet => {
      const isDifferent =
        newSet.size !== currentSet.size ||
        Array.from(newSet).some(item => !currentSet.has(item));

      return isDifferent ? newSet : currentSet;
    });
  }, [defaultExpandedSections]);

  // Call onChange when expandedSections changes due to user interaction
  useEffect(() => {
    if (userInteractionRef.current && onChange) {
      onChange(expandedSections);
      userInteractionRef.current = false;
    }
  }, [expandedSections, onChange]);

  // Toggle function - marks change as user interaction
  const toggleSection = useCallback((sectionId: string) => {
    userInteractionRef.current = true;
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
        // Add to animating sections for expand animation
        setAnimatingSections(prevAnim => new Set(prevAnim).add(sectionId));
        // Remove from animating after animation completes (200ms)
        setTimeout(() => {
          setAnimatingSections(prevAnim => {
            const newAnim = new Set(prevAnim);
            newAnim.delete(sectionId);
            return newAnim;
          });
        }, 250);
      }
      return newSet;
    });
  }, []);

  const expandSection = useCallback((sectionId: string) => {
    userInteractionRef.current = true;
    setExpandedSections(prev => new Set(prev).add(sectionId));
  }, []);

  const collapseSection = useCallback((sectionId: string) => {
    userInteractionRef.current = true;
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      newSet.delete(sectionId);
      return newSet;
    });
  }, []);

  const expandAll = useCallback((sectionIds: string[]) => {
    userInteractionRef.current = true;
    setExpandedSections(new Set<string>(sectionIds));
  }, []);

  const collapseAll = useCallback(() => {
    userInteractionRef.current = true;
    setExpandedSections(new Set<string>());
  }, []);

  const isExpanded = useCallback((sectionId: string) => {
    return expandedSections.has(sectionId);
  }, [expandedSections]);

  const isAnimating = useCallback((sectionId: string) => {
    return animatingSections.has(sectionId);
  }, [animatingSections]);

  return {
    expandedSections,
    toggleSection,
    expandSection,
    collapseSection,
    expandAll,
    collapseAll,
    isExpanded,
    isAnimating
  };
};

export default useAccordionState;